import { useState, useEffect, useRef, useContext } from 'react';
import { formatDistance } from 'date-fns';
import { da } from 'date-fns/locale';
import { UserInfoContext, EmployeesContext } from '../../context';
import Avatar from '../../components/avatar/avatar';
import { Panel, Panels } from '../../components/panel/panel';

const isDayToday = (date) => {
  const today = new Date();

  const todayDay = today.getDate();
  const todayMonth = today.getMonth();

  const targetDay = date.getDate();
  const targetMonth = date.getMonth();

  return todayDay === targetDay && todayMonth === targetMonth;
};

export default function DashboardOccasions() {
  const isInitialLoad = useRef(true);
  const userInfo = useContext(UserInfoContext);
  const employees = useContext(EmployeesContext);
  const [occasions, setOccasions] = useState([]);

  useEffect(() => {
    const fetchOccasions = async () => {
      if (employees.length !== 0 && userInfo?.companyId) {
        const allOccasions = [];
        const today = new Date();

        employees.forEach((employee) => {
          if (!employee.data) return;
          if (!employee.data.dateOfBirth || !employee.data.startDate) return;
          if (employee.data.employmentStatus === 'inactive') return;

          // Birthday occasion
          const dateOfBirth = new Date(employee.data.dateOfBirth.toDate());
          let birthDayNext = new Date(
            today.getFullYear(),
            dateOfBirth.getMonth(),
            dateOfBirth.getDate(),
          );
          if (birthDayNext < today) {
            birthDayNext = new Date(
              today.getFullYear() + 1,
              dateOfBirth.getMonth(),
              dateOfBirth.getDate(),
            );
          }
          const birthDayRelative = formatDistance(birthDayNext, today, {
            addSuffix: true,
            locale: da,
          });
          allOccasions.push({
            link: `/employees/${employee.id}`,
            title: isDayToday(birthDayNext)
              ? `${employee.data.firstName} ${employee.data.lastName} har fødselsdag 🇩🇰`
              : `${employee.data.firstName} ${employee.data.lastName} har snart fødselsdag`,
            text: isDayToday(birthDayNext)
              ? `${employee.data.firstName} fylder ${
                  birthDayNext.getFullYear() - dateOfBirth.getFullYear()
                } år i dag`
              : `${employee.data.firstName} fylder ${
                  birthDayNext.getFullYear() - dateOfBirth.getFullYear()
                } år\n\n${birthDayRelative}`,
            avatar: (
              <Avatar
                firstName={employee.data.firstName}
                lastName={employee.data.lastName}
                profilePhotoUrl={employee.data.profilePhotoUrl}
              />
            ),
            date: birthDayNext,
          });

          // Work anniversary occasion
          const startDate = new Date(employee.data.startDate.toDate());
          if (startDate <= today) {
            let anniversaryNext = new Date(
              today.getFullYear(),
              startDate.getMonth(),
              startDate.getDate(),
            );
            if (anniversaryNext < today) {
              anniversaryNext = new Date(
                today.getFullYear() + 1,
                startDate.getMonth(),
                startDate.getDate(),
              );
            }
            const anniversaryRelative = formatDistance(anniversaryNext, today, {
              addSuffix: true,
              locale: da,
            });
            allOccasions.push({
              link: `/employees/${employee.id}`,
              title: isDayToday(anniversaryNext)
                ? `${employee.data.firstName} ${employee.data.lastName} har jubilæum 🎉`
                : `${employee.data.firstName} ${employee.data.lastName} har snart jubilæum`,
              text: isDayToday(anniversaryNext)
                ? `${employee.data.firstName} har været ansat i ${
                    anniversaryNext.getFullYear() - startDate.getFullYear()
                  } år i dag`
                : `${employee.data.firstName} har været ansat i ${
                    anniversaryNext.getFullYear() - startDate.getFullYear()
                  } år\n\n${anniversaryRelative}`,
              avatar: (
                <Avatar
                  firstName={employee.data.firstName}
                  lastName={employee.data.lastName}
                  profilePhotoUrl={employee.data.profilePhotoUrl}
                />
              ),
              date: anniversaryNext,
            });
          }
        });

        // Sort all occasions by date (soonest first)
        allOccasions.sort((a, b) => a.date - b.date);

        // Filter for those happening today or within 30 days
        const filtered = allOccasions.filter((occasion) => {
          const diff = (occasion.date - today) / (1000 * 60 * 60 * 24);
          return diff >= 0 && diff <= 30;
        });

        // If fewer than 3, add more from the sorted list until there are at least 3
        let finalOccasions = [...filtered];
        if (finalOccasions.length < 3) {
          const extras = allOccasions.filter(
            (occasion) => !filtered.includes(occasion),
          );
          for (let i = 0; finalOccasions.length < 3 && i < extras.length; i++) {
            finalOccasions.push(extras[i]);
          }
        }

        setOccasions(finalOccasions);
        isInitialLoad.current = false;
      }
    };

    fetchOccasions();
  }, [userInfo, employees]);

  if (occasions.length === 0 && !isInitialLoad.current) {
    return (
      <div className="dashboard-panel-empty">
        <span className="dashboard-panel-empty__icon">🔖</span>
        Ingen opkommende mærkedage
      </div>
    );
  }
  return (
    <Panels
      noMargin
      style={{
        gap: '15px',
      }}
    >
      {occasions.map((occasion, index) => (
        <Panel noPadding key={index}>
          <div className="dashboard-occasion-item">
            {occasion.avatar}
            <div className="dashboard-occasion-item__person">
              <h5>{occasion.title}</h5>
              <p>{occasion.text}</p>
            </div>
          </div>
        </Panel>
      ))}
    </Panels>
  );
}
